import { Suspense } from "react";

import { defineMessages } from "react-intl";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { AppPricingData } from "../../../../../contentful/index.tsx";
import { offeringSkus } from "../constants.ts";

export interface TeamworkCollectionPriceFinderProps {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
}

export const TeamworkCollectionPriceFinder = (
    props: TeamworkCollectionPriceFinderProps
) => {
    const content = {
        free: usePricingPlanComparison("Free"),
        standard: usePricingPlanComparison("Standard"),
        premium: usePricingPlanComparison("Premium"),
        enterprise: usePricingPlanComparison("Enterprise")
    };

    const freePlanFeatureMessages = createPlanFeatureMessages(content, "free");

    const standardPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "standard"
    );

    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "premium"
    );
    const enterprisePlanFeatureMessages = createPlanFeatureMessages(
        content,
        "enterprise"
    );

    // planNames should not be translated
    const productPlans: ProductPlan[] = [
        {
            planName: "Free",
            offeringSku: offeringSkus.free,
            // @ts-ignore
            description: content.free.planOneLiner,
            recommendationLimit: 0,
            signupLimit: 10,
            planFeatureMessages: freePlanFeatureMessages,
            signupURLInfo: {
                shouldLocalize: true,
                urlDomain: window.location.origin,
                urlPath: "/collections/teamwork#contact"
            }
        },
        {
            planName: "Standard",
            offeringSku: offeringSkus.standard,
            // @ts-ignore
            description: content.standard.planOneLiner,
            recommendationLimit: 0,
            signupLimit: 100_000,
            planFeatureMessages: standardPlanFeatureMessages,
            signupURLInfo: {
                shouldLocalize: true,
                urlDomain: window.location.origin,
                urlPath: "/collections/teamwork#contact"
            }
        },
        {
            planName: "Premium",
            offeringSku: offeringSkus.premium,
            // @ts-ignore
            description: content.premium.planOneLiner,
            recommendationLimit: 0,
            signupLimit: 100_000,
            planFeatureMessages: premiumPlanFeatureMessages,
            signupURLInfo: {
                shouldLocalize: true,
                urlDomain: window.location.origin,
                urlPath: "/collections/teamwork#contact"
            }
        },
        {
            planName: "Enterprise",
            offeringSku: offeringSkus.enterprise,
            // @ts-ignore
            description: content.enterprise.planOneLiner,
            recommendationLimit: 5000,
            signupLimit: 100_000,
            planFeatureMessages: enterprisePlanFeatureMessages,
            signupURLInfo: {
                shouldLocalize: true,
                urlDomain: window.location.origin,
                urlPath: "/collections/teamwork#contact"
            }
        }
    ];

    const messages: PriceFinderMessages = defineMessages({
        unitCountInputTextRedesign: {
            id: "TeamworkCollectionPriceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "TeamworkCollectionPriceFinder.unitCountInputText",
            defaultMessage: "users"
        },
        cardEnterpriseMinimumCount: {
            id: "TeamworkCollectionPriceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardOverSignupLimit: {
            id: "TeamworkCollectionPriceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardUnitCountTierRange: {
            id: "TeamworkCollectionPriceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "TeamworkCollectionPriceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user/month"
        }
    });

    return (
        <Suspense>
            <PriceFinderContextProvider
                dataProductKey="teamworkCollection"
                productPlans={productPlans}
                defaultUnitCount={300}
            >
                <PriceFinderSection
                    messages={messages}
                    serverSideCCPPricing={
                        props.pricing?.ccpPricingData?.teamworkCollection
                    }
                    countryCode={props.countryCode}
                />
            </PriceFinderContextProvider>
        </Suspense>
    );
};
